import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaintBrush, faLightbulb } from '@fortawesome/free-solid-svg-icons';

interface Props {
  sketchItem: string;
  hint: string;
  roundNumber: number;
  totalRounds: number;
  timeLeft: number;
}

const SketcherDrawScreen: React.FC<Props> = ({
  sketchItem,
  hint,
  roundNumber,
  totalRounds,
  timeLeft,
}) => {
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const timeDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  const isUrgent = timeLeft <= 10;

  return (
    <div className="sketcher-draw">
      {/* Top bar */}
      <div className="sketcher-draw__topbar">
        <span className="sketcher-draw__round">
          Round {roundNumber} of {totalRounds}
        </span>
        <div className={`sketcher-draw__timer ${isUrgent ? 'sketcher-draw__timer--urgent' : ''}`}>
          <span className="sketcher-draw__timer-value">{timeDisplay}</span>
        </div>
      </div>

      {/* Main content area - distributed across the screen */}
      <div className="sketcher-draw__body">
        {/* Left side: Object info displayed prominently */}
        <div className="sketcher-draw__info">
          <div className="sketcher-draw__object-card">
            <div className="sketcher-draw__object-icon">
              <FontAwesomeIcon icon={faPaintBrush} />
            </div>
            <h1 className="sketcher-draw__object-name">{sketchItem}</h1>
            <p className="sketcher-draw__object-label">Draw this!</p>
          </div>

          <div className="sketcher-draw__hint-card">
            <div className="sketcher-draw__hint-icon">
              <FontAwesomeIcon icon={faLightbulb} />
            </div>
            <div className="sketcher-draw__hint-content">
              <span className="sketcher-draw__hint-label">Hint</span>
              <p className="sketcher-draw__hint-text">{hint}</p>
            </div>
          </div>

          {/* Large countdown for visibility */}
          <div className="sketcher-draw__countdown">
            <span className={`sketcher-draw__countdown-value ${isUrgent ? 'sketcher-draw__countdown-value--urgent' : ''}`}>
              {timeDisplay}
            </span>
            <span className="sketcher-draw__countdown-label">Time Remaining</span>
          </div>
        </div>

        {/* Right side: Canvas area */}
        <div className="sketcher-draw__canvas-area">
          <div className="sketcher-draw__canvas">
            <div className="sketcher-draw__canvas-placeholder">
              {/* Animated "pen drawing a sketch" scene */}
              <svg
                className="sketcher-draw__sketch"
                viewBox="0 0 200 160"
                role="img"
                aria-label="A pen drawing a sketch"
              >
                {/* The line that draws itself (a simple squiggle/shape) */}
                <path
                  className="sketcher-draw__sketch-line"
                  d="M30 110 C 50 40, 90 40, 100 90 S 150 140, 170 70"
                  fill="none"
                  stroke="var(--color-primary)"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* The pen that tracks the drawing tip */}
                <g className="sketcher-draw__pen">
                  {/* pen body (angled), tip at the group's origin (0,0) */}
                  <line
                    x1="0"
                    y1="0"
                    x2="18"
                    y2="-26"
                    stroke="var(--color-text)"
                    strokeWidth="6"
                    strokeLinecap="round"
                  />
                  {/* pen nib highlight */}
                  <circle
                    cx="0"
                    cy="0"
                    r="3"
                    fill="var(--color-primary)"
                  />
                </g>
              </svg>
              <p className="sketcher-draw__canvas-text">Players are drawing...</p>
              <p className="sketcher-draw__canvas-sub">Sketches will appear in the gallery once time is up</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SketcherDrawScreen;
